import { inject } from '@angular/core';
import { RedirectCommand, Router } from '@angular/router';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { firstValueFrom, forkJoin, filter, take, combineLatest } from 'rxjs';
import { FieldsService } from './core/fields/fields.service';
import { ShortcutService } from './core/services/shortcut.service';
import { DefaultValuesService } from './core/services/default-values.service';
import { TaskNotificationService } from './core/services/task-notification.service';
import { InitAppService } from './core/services/init-app.service';
import { VersionCheckService } from './core/services/version-check.service';
import { FieldsStore } from './store/fields.store';
import { MultiTenantService } from './core/services/multi-tenant.service';
import { UserService } from './core/user/user.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { UserSettingsService } from './core/services/user-settings.service';
import { AuthService } from './core/auth/auth.service';
import {
	bootstrapIdentityRedirectSessionKey,
	isBootstrapIdentityEnabled,
	tokenHasRequiredIdentityClaims,
} from './core/auth/bootstrap-identity.utils';

export const initialDataResolver = async () => {
	const services = {
		navigation: inject(NavigationService),
		fields: inject(FieldsService),
		shortcuts: inject(ShortcutService),
		defaultValues: inject(DefaultValuesService),
		taskNotification: inject(TaskNotificationService),
		multiTenant: inject(MultiTenantService),
		initApp: inject(InitAppService),
		versionCheck: inject(VersionCheckService),
		fieldsStore: inject(FieldsStore),
		userService: inject(UserService),
		auth0: inject(Auth0Service),
		userSettings: inject(UserSettingsService),
		auth: inject(AuthService),
		router: inject(Router),
	};

	// Create observable from signal in injection context (before any await)
	const userSig$ = toObservable(services.userService.userSig$);

	try {
		// Wait for Auth0 to be ready and authenticated
		const [, , auth0User, accessToken] = await firstValueFrom(
			combineLatest([
				services.auth0.isLoading$,
				services.auth0.isAuthenticated$,
				services.auth0.user$,
				services.auth0.getAccessTokenSilently(),
			]).pipe(
				filter(([loading, isAuth]) => !loading && !!isAuth),
				take(1)
			)
		);

		// Bootstrap user from Auth0 if not already set
		if (!services.userService.userSig$() && auth0User && accessToken) {
			const canRedirectToBootstrap = isBootstrapIdentityEnabled();
			const hasRequiredClaims = tokenHasRequiredIdentityClaims(accessToken);

			if (!hasRequiredClaims && canRedirectToBootstrap) {
				sessionStorage.setItem(bootstrapIdentityRedirectSessionKey, '1');
				return new RedirectCommand(
					services.router.parseUrl('/bootstrap-identity')
				);
			}

			services.auth.setTokenFromAuth0(auth0User, accessToken);

			// Fail fast if user bootstrap failed (e.g., missing JWT claims)
			if (!services.userService.userSig$()) {
				throw new Error(
					'Failed to bootstrap user from Auth0 token. ' +
						'The JWT may be missing required claims.'
				);
			}
		}

		// Clear any stale bootstrap redirect flag after successful user load.
		sessionStorage.removeItem(bootstrapIdentityRedirectSessionKey);

		// Wait for user to be available
		await firstValueFrom(
			userSig$.pipe(
				filter((user) => !!user),
				take(1)
			)
		);

		// Handle initialization app version check
		services.versionCheck.initVersionCheck('./version.json');

		// Handle initialization tasks
		services.taskNotification.init();

		// Handle initialization multi-tenant
		services.multiTenant.init();

		// Load all data in parallel
		const data = await firstValueFrom(
			forkJoin({
				navigation: services.navigation.get(),
				userSettings: services.userSettings.get(),
				fields: services.fields.get(),
				shortcuts: services.shortcuts.get(),
				defaultValues: services.defaultValues.get(),
			})
		);
		// Initialize app with the loaded data
		await firstValueFrom(services.initApp.init());

		return data;
	} catch (error) {
		console.error('Resolver error:', error);
		// Handle error appropriately
		throw error; // or return default values
	}
};
